import { useState, useRef, useEffect, useCallback } from 'react';
import { useSellerMode } from '../../contexts/SellerModeContext';
import { Eye, SignOut, MagnifyingGlass, CaretDown, Spinner } from '@phosphor-icons/react';

export default function SellerModeSelector() {
  const { isSellerMode, activeSeller, sellers, loadingSellers, fetchSellers, enterSellerMode, exitSellerMode } = useSellerMode();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleOpen = async () => {
    if (!open) {
      await fetchSellers('');
      setSearch('');
    }
    setOpen(!open);
  };

  const handleSearch = useCallback((val) => {
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSellers(val), 300);
  }, [fetchSellers]);

  if (isSellerMode) {
    return (
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-[#f6a609]/10 border border-[#f6a609]/30">
          <Eye size={15} className="text-[#f6a609]" weight="bold" />
          <span className="text-xs font-semibold text-[#d18f08] max-w-[140px] truncate">{activeSeller?.name}</span>
        </div>
        <button onClick={exitSellerMode} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#ea4b5a]/10 border border-[#ea4b5a]/30 text-[#ea4b5a] text-xs font-semibold hover:bg-[#ea4b5a]/20 transition-colors" data-testid="exit-seller-mode-btn">
          <SignOut size={14} weight="bold" /> Exit
        </button>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button onClick={handleOpen} className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-[#e5e5ea] bg-white hover:bg-[#fcfcfc] text-sm text-[#5a5c87] transition-colors" data-testid="seller-mode-trigger">
        <Eye size={15} className="text-[#7d64ff]" weight="bold" />
        <span className="text-xs font-semibold">View as Seller</span>
        <CaretDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl border border-[#e5e5ea] shadow-lg z-50 overflow-hidden" data-testid="seller-mode-dropdown">
          <div className="p-3 border-b border-[#f0f0f0]">
            <div className="relative">
              <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9c9db7]" size={15} />
              <input type="text" value={search} onChange={e => handleSearch(e.target.value)}
                placeholder="Search 500+ sellers..."
                className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-[#e5e5ea] bg-[#fcfcfc] outline-none focus:border-[#7d64ff] transition-colors"
                autoFocus data-testid="seller-mode-search" />
            </div>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {loadingSellers ? (
              <div className="flex items-center justify-center p-4 gap-2 text-[#9c9db7] text-sm">
                <Spinner size={14} className="animate-spin" /> Loading sellers...
              </div>
            ) : sellers.length === 0 ? (
              <div className="p-4 text-center text-[#9c9db7] text-sm">No sellers found</div>
            ) : (
              sellers.filter(s => s.role === 'seller').map(seller => (
                <button key={seller.user_id}
                  onClick={() => { enterSellerMode(seller); setOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-[#f8f7ff] transition-colors text-left"
                  data-testid={`seller-mode-option-${seller.user_id}`}
                >
                  <div className="w-8 h-8 rounded-full bg-[#7d64ff]/10 text-[#7d64ff] flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {(seller.name || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#0A0A0A] truncate">{seller.name}</p>
                    <p className="text-[11px] text-[#9c9db7] truncate">{seller.email}{seller.business_name ? ` · ${seller.business_name}` : ''}</p>
                  </div>
                </button>
              ))
            )}
          </div>

          <div className="p-2 border-t border-[#f0f0f0] bg-[#fcfcfc]">
            <p className="text-[10px] text-[#9c9db7] text-center">Showing {sellers.filter(s => s.role === 'seller').length} sellers · Type to search</p>
          </div>
        </div>
      )}
    </div>
  );
}
